import { trpc, trpcApi } from '@bitbot/api';
import { createGlobalStore } from 'hox';
import { useState, useRef, useCallback, useEffect } from 'react';
import { useToken } from './token';
import { useEmail } from './email';
import { Notify } from '@bitbot/ui-design';
import { debounce } from 'lodash';
import { useProfile } from './profile';
import { useRouter } from 'next/router';
import { useWallets } from '../wallet';
import { useGlobalValueStore } from '../trade/common';
import { isTelegramWeb } from '@bitbot/shared/bridge';

export const [useLogin] = createGlobalStore(() => {
  const { token, setToken } = useToken();

  const retryTimesRef = useRef(0);
  const [showReload, setShowReload] = useState(false);
  const loginByInitRawMutation = trpc.user.login.useMutation();

  const loginByInitRaw = async (initDataRaw: string, betaCode: string) => {
    try {
      const res = isTelegramWeb() ? await loginByInitRawMutation.mutateAsync({
        initDataRaw,
        betaCode,
      }) : ({ token: `guest_feel_good_${'202411210800'}` });
      // TODO：上文会去验证TG的一些权限，先给个恒定的，也不做登录啥的
      setToken(res.token);
      setTimeout(() => {
        const utm = new URLSearchParams(window.location.search).get(
          'tgWebAppStartParam',
        );
        if (
          utm &&
          utm !== '' &&
          utm !== 'tg_welcome' &&
          !utm.startsWith('page_')
        ) {
          trpcApi.user.setUtmSource.query({ utmSource: utm });
        }
      }, 1000);
      retryTimesRef.current === 0;
    } catch (err) {
      // 异常退出登录
      retryTimesRef.current++;
      if (retryTimesRef.current >= 3) {
        setShowReload(true);
      } else {
        if (token) {
          setToken('');
        }
        setTimeout(() => {
          loginByInitRaw(initDataRaw, betaCode);
        }, 500);
      }
    }
  };

  const setUtmSource = (utm: string | null) => {
    if (utm && utm !== '') {
      trpcApi.user.setUtmSource.query({ utmSource: utm });
    }
  };

  return {
    token,
    loginByInitRaw,
    showReload,
    setUtmSource,
  };
});

export const useLogout = () => {
  const {
    setEmail,
    emailCode,
    setEmailCode,
    countDown,
    setCountDown,
    hasSent,
    setHasSent,
    authCode,
    setAuthCode,
    showStatus,
    setShowStatus,
    status,
    setIsSendingCode,
    isSendingCode,
    otpCodeError,
    setOtpCodeError,
    setEmailCodeError,
    emailCodeError,
    infoRef,
  } = useEmail();
  const { setRecentWallets, setShowBalance } = useGlobalValueStore();
  const { setTradeToken } = useToken();
  const { setWallets, setWalletsInfo, setWalletCache, setCurrentWallet } =
    useWallets();
  const router = useRouter();
  const { email } = useProfile();
  const [isLogingout, setIsLogingout] = useState(false);
  useEffect(() => {
    setEmail(email!);
  }, [email]);

  const removeLoginCache = (unbindOrEmailLogin = false) => {
    setTradeToken(undefined);
    setWalletsInfo(undefined);
    setWallets([]);
    if (unbindOrEmailLogin) {
      setRecentWallets([]);
      setShowBalance(true);
      setWalletCache(undefined);
      setCurrentWallet(undefined);
    }
  };

  const logout = useCallback(
    debounce(() => {
      setIsLogingout(true);
      trpcApi.user.logoutWithEmail
        .query({
          email: infoRef.current?.email!,
          otp: infoRef.current?.authCode!,
          code: infoRef.current?.emailCode!,
        })
        .then(res => {
          if (res) {
            removeLoginCache(true);
            router.replace('/');
          } else {
            Notify.error('Log out failed, please try again later');
          }
        })
        .catch(e => {
          if (e.message === 'Invalid OTP') {
            setOtpCodeError(true);
          } else if (e.message === 'Email Code invalid or expired') {
            setEmailCodeError(true);
          } else {
            Notify.error(e.message);
          }
        })
        .finally(() => {
          setIsLogingout(false);
        });
    }, 300),
    [],
  );

  const sendCode = useCallback(
    debounce(() => {
      if (status.emailStatus) {
        setIsSendingCode(true);
        trpcApi.user.sendCodeEmail
          .query({ email: email! })
          .then(data => {
            if (data) {
              setHasSent(true);
            } else {
              Notify.error('Send code failed, please try again later');
            }
          })
          .catch(e => {
            Notify.error(e.message);
          })
          .finally(() => {
            setIsSendingCode(false);
          });
      }
    }, 300),
    [email, status],
  );

  const handleInvalidToken = (message: string) => {
    if (message.includes('invalid tradingToken')) {
      removeLoginCache();
      router.replace('/');
    } else {
      Notify.error(message);
    }
  };

  return {
    sendCode,
    otpCodeError,
    setOtpCodeError,
    setEmailCodeError,
    emailCodeError,
    setEmail,
    emailCode,
    setEmailCode,
    countDown,
    setCountDown,
    hasSent,
    setHasSent,
    authCode,
    setAuthCode,
    showStatus,
    setShowStatus,
    status,
    isSendingCode,
    logout,
    removeLoginCache,
    handleInvalidToken,
    isLogingout,
  };
};
